import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  List,
  ListItem,
  Paper,
  IconButton,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ClearOutlined from '@mui/icons-material/ClearOutlined';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ClickAwayListener from '@mui/material/ClickAwayListener';
 

const TOTAL_STEPS = 8;

const usCities = [
  "New York, New York", "Los Angeles, California", "Chicago, Illinois", "Houston, Texas",
  "Phoenix, Arizona", "Philadelphia, Pennsylvania", "San Antonio, Texas", "San Diego, California",
  "Dallas, Texas", "San Jose, California", "Austin, Texas", "Jacksonville, Florida",
  "Fort Worth, Texas", "Columbus, Ohio", "Charlotte, North Carolina", "San Francisco, California",
  "Indianapolis, Indiana", "Seattle, Washington", "Denver, Colorado", "Washington, District of Columbia",
  "Boston, Massachusetts", "Nashville, Tennessee", "El Paso, Texas", "Detroit, Michigan",
  "Las Vegas, Nevada", "Portland, Oregon", "Memphis, Tennessee", "Louisville, Kentucky",
  "Baltimore, Maryland", "Milwaukee, Wisconsin"
];

const Step8Location = ({ onBack, onNext, currentStep = 8, showHeader = true, className }) => {
  const [city, setCity] = useState('');
  const [filteredCities, setFilteredCities] = useState([]);
  const [status, setStatus] = useState(null); // 'success' | 'error' | null
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const renderSegmentedProgressBar = () => (
    <Box display="flex" gap={1} mb={1}>
      {Array.from({ length: TOTAL_STEPS }).map((_, index) => (
        <Box
          key={index}
          flex={1}
          height={4}
          borderRadius={5}
          sx={{
            backgroundColor: index < currentStep ? '#0D99FF' : '#e0e0e0',
            transition: 'background-color 0.3s ease',
          }}
        />
      ))}
    </Box>
  );

  useEffect(() => {
    if (city.trim()) {
      const filtered = usCities.filter((c) =>
        c.toLowerCase().includes(city.toLowerCase())
      );
      setFilteredCities(filtered);
      setShowDropdown(filtered.length > 0 && !usCities.includes(city));
      if (usCities.includes(city)) {
        setStatus('success');
      } else {
        setStatus('error');
      }
    } else {
      setFilteredCities([]);
      setStatus(null);
      setShowDropdown(false);
    }
  }, [city]);

  const handleSelectCity = (selectedCity) => {
    setCity(selectedCity);
    setFilteredCities([]);
    setShowDropdown(false);
    setStatus('success');
  };

  const handleUseMyLocation = () => {
    setLoading(true);

    // Add delay before fetching location
    setTimeout(() => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            try {
              const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
              );
              const data = await response.json();
              const locationName =
                data?.address?.city ||
                data?.address?.town ||
                data?.address?.village ||
                '';
              const state = data?.address?.state || '';
              if (locationName) {
                const detectedCity = `${locationName}, ${state}`;
                setCity(detectedCity);
                setStatus(usCities.includes(detectedCity) ? 'success' : 'error');
              }
            } catch (err) {
              console.error('Error fetching location:', err);
              setStatus('error');
            } finally {
              setLoading(false);
            }
          },
          () => {
            setLoading(false);
            setStatus('error');
          }
        );
      } else {
        setLoading(false);
        setStatus('error');
      }
    }, 5000); // Delay of 2 seconds
  };



  const handleContinue = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      onNext(city);
    }, 1500);
  };


  return (
    <Box display="flex" alignItems="start" justifyContent="center" className={className} sx={{ paddingTop: { xs: 0, sm: 13, md: 6, }, paddingBottom: { xs: 0, sm: 3, md: 6, }, }}>
      <Paper elevation={3} sx={{ p: { xs: '20px 15px', sm: 4, }, maxWidth: 480, width: '100%', borderRadius: { xs: '0', sm: '8px', md: '8px', boxShadow: 'none', }, position: 'relative', }}>
        {showHeader && (
        <>
        {renderSegmentedProgressBar()}

        <IconButton onClick={onBack}>
          <ArrowBackIosNewIcon />
        </IconButton>
       
        </> )}
        <Typography variant="h5" fontWeight={600} mb={2} align="center" fontFamily="ivypresto-headline, serif">
          Where Are You Located?
        </Typography>

        <Typography variant="body2" color='rgba(0, 0, 0, 0.7)' mb={2} align="center">
          Enter your city or postal code
        </Typography>

        <ClickAwayListener onClickAway={() => setShowDropdown(false)}>
          <Box>
            <TextField
              label="Your Location*"
              fullWidth
              variant="outlined"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              onFocus={() => {
                if (filteredCities.length > 0 && !usCities.includes(city)) {
                  setShowDropdown(true);
                }
              }}
              sx={{ mb: 2 }}
              InputLabelProps={{
                sx: {
                  top: '-5px',
                  fontSize: '14px',
                  '&.MuiInputLabel-shrink': {
                    top: '0px',
                    transform: 'translate(14px, -8px) scale(0.75)',
                  },
                },
              }}
              InputProps={{
                sx: {
                  height: 40,
                  borderRadius: '8px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: status === 'error' ? '#F45B39' : '#ccc',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: status === 'error' ? '#F45B39' : '#999',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: status === 'error' ? '#F45B39' : '#0D99FF',
                    borderWidth: '2px',
                  },
                  '& .MuiInputBase-input': {
                    padding: '0px 12px',
                    fontSize: '14px',
                  },
                  '& input:-webkit-autofill': {
                    WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                    WebkitTextFillColor: '#000 !important',
                  },
                },
              }}
            />

            {showDropdown && filteredCities.length > 0 && (
              <Paper elevation={2} sx={{ maxHeight: 280, overflow: 'auto', mb: 2, borderRadius: '8px' }}>
                <List dense>
                  {filteredCities.map((c, index) => (
                    <ListItem
                      button
                      key={index}
                      onClick={() => handleSelectCity(c)}
                      sx={{
                        fontSize: '13px',
                        paddingY: '6px',
                        paddingX: '12px',
                      }}
                    >
                      {c}
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </Box>
        </ClickAwayListener>

        {status && (
          <Box
            bgcolor={status === 'success' ? '#02BC7D' : '#F45B39'}
            sx={{
              borderRadius: '13px',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 300,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              padding: '8px 15px',
              mb: 2,
            }}
          >
            {status === 'success' ? (
              <CheckCircleOutlineOutlinedIcon sx={{ color: '#fff', fontSize: 18 }} />
            ) : (
              <ClearOutlined sx={{ color: '#fff', fontSize: 18 }} />
            )}

            <Typography sx={{ fontSize: '13px', fontWeight: '300' }}>
              {status === 'success'
                ? 'Location selected successfully.'
                : 'Please choose a valid city from the list.'}
            </Typography>
          </Box>
        )}

        <Button
          startIcon={loading ? null : <MyLocationIcon />}
          onClick={handleUseMyLocation}
          variant="outlined"
          fullWidth
          sx={{
            textTransform: 'none',
            border: 'none',
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'transparent',
              border: 'none',
            },
            '&:focus': {
              backgroundColor: 'transparent',
              border: 'none',
              outline: 'none',
              boxShadow: 'none',
            },
            '&.Mui-disabled': {
              backgroundColor: 'transparent',
              border: 'none',
            },
          }}
          disabled={loading}
        >
          {loading ? (
            <>
              {/* Wave background effect */}
              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  background: 'rgba(255, 0, 0, 0.1)',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 0,
                  animation: 'wavePulse 2.5s infinite',
                  '@keyframes wavePulse': {
                    '0%': {
                      transform: 'translate(-50%, -50%) scale(0.8)',
                      opacity: 0.4,
                    },
                    '50%': {
                      transform: 'translate(-50%, -50%) scale(1.3)',
                      opacity: 0.2,
                    },
                    '100%': {
                      transform: 'translate(-50%, -50%) scale(1.6)',
                      opacity: 0,
                    },
                  },
                }}
              />

              {/* Pulse Red */}
              <Box
                component="img"
                src="/assets/images/signup-steps/img-red.png"
                sx={{
                  position: 'absolute',
                  left: '50%',
                  width: '18px',
                  transform: 'translateX(-50%)',
                  animation: 'pulseRed 3s infinite',
                  zIndex: 1,
                  '@keyframes pulseRed': {
                    '0%': { opacity: 0, transform: 'translateX(-50%) scale(0.8)' },
                    '50%': { opacity: 1, transform: 'translateX(-50%) scale(1.2)' },
                    '100%': { opacity: 0, transform: 'translateX(-50%) scale(0.8)' },
                  },
                }}
              />

              {/* Pulse Green */}
              <Box
                component="img"
                src="/assets/images/signup-steps/img-green.png"
                sx={{
                  position: 'absolute',
                  left: '50%',
                  width: '12px',
                  transform: 'translateX(-50%)',
                  animation: 'pulseGreen 3s infinite 1.5s',
                  zIndex: 1,
                  '@keyframes pulseGreen': {
                    '0%': { opacity: 0, transform: 'translateX(-50%) scale(0.8)' },
                    '50%': { opacity: 1, transform: 'translateX(-50%) scale(1.2)' },
                    '100%': { opacity: 0, transform: 'translateX(-50%) scale(0.8)' },
                  },
                }}
              />

              {/* Loading Text */}
              <Typography
                component="span"
                sx={{
                  position: 'relative',
                  zIndex: 2,
                  color: 'inherit',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  paddingTop: '90px',
                  fontSize: '13px',
                }}
              >
                Fetching Your Location...
              </Typography>
            </>
          ) : (
            'Use My Current Location'
          )}
        </Button>


        <Button
          variant="contained"
          fullWidth
          sx={{
            mt: 3,
            height: 45,
            backgroundColor: loading ? '#A1887F' : '#5F3D23',
            color: '#fff',
            textTransform: 'none',
            borderRadius: '8px',
            fontWeight: 600,
            '&:hover': {
              bgcolor: loading ? '#A1887F' : '#2B1E1A',
            },
          }}
          onClick={handleContinue}
          disabled={!city || status !== 'success' || loading}
        >
          {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Continue'}
        </Button>

      </Paper>
    </Box>
  );
};

export default Step8Location;
